import React, { useState } from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDriverTotalDistanceAction, resetDriverTotalDistance, } from 'state/actions/deliveryActions';
import {
    Box,
    Flex,
    Text,
    Icon,
    Divider,
    Stack,
    Tooltip,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    IconButton,
    Avatar
} from '@chakra-ui/react';
import { BadgeCheck, BadgeInfo, BadgeX, Download, Calendar, MapPin, Package, Gauge, IdCard,
    Wallet,
    CreditCard,
    Coins,
    Truck,
 } from 'lucide-react';
import PdfIcon from "assets/svg/pdf.png";
import WordIcon from "assets/svg/word.png";
import ImageIcon from "assets/svg/jpg.png";
import NoDataFound from 'components/NoDataGraphics/NoDataFound';
import DocumentViewer from 'components/DocumentViewer/DocumentViewer';
import { formatDate } from 'utils/dateFormatter';
import ReusableAvatar from 'components/Avatar/ReusableAvatar';
import InfoRow from 'components/InfoRow/InfoRow';
import GradientButton from 'components/Buttons/GradientButton';

const DocumentIcon = ({ type }) => {
    switch (type) {
        case "pdf":
            return <img src={PdfIcon} alt="PDF Icon" width={24} height={24} />;
        case "docx":
            return <img src={WordIcon} alt="Word Icon" width={24} height={24} />;
        case "jpeg":
        case "jpg":
        case "png":
            return <img src={ImageIcon} alt="Image Icon" width={24} height={24} />;
        default:
            return null;
    }
};

const ViewDriver = ({ driver, onClose,  onTopUp }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentDocument, setCurrentDocument] = useState(null);
    const dispatch = useDispatch();

    const {
        driverTotalDistance,
        driverDistanceLoading,
    } = useSelector((state) => state.deliveries);

    if (!driver || !driver.driver) return null;
    const wallet = driver?.wallet;

    const {
        license_number,
        license_expiry_date,
        route_preference,
        package_preference,
        created_at,
        status,
        user,
    } = driver.driver;
    const driverId = driver?.driver?.driver_id;
    useEffect(() => {

        dispatch(resetDriverTotalDistance());

        if (driverId) {
            dispatch(
                fetchDriverTotalDistanceAction(driverId)
            );
        }

    }, [dispatch, driverId]);

    // Documents are from user
    const excludedDocumentTypes = [
        "Chat Image",
        "Payment Proof",
        "Package Evidence Image",
        "Chat Document",
    ];

    const documents = Object.values(
        (user?.documents || [])
            .filter(
                (doc) =>
                    !excludedDocumentTypes.includes(doc.document_type)
            )
            .reduce((acc, doc) => {
                const existing = acc[doc.document_type];

                // keep newest uploaded document
                if (
                    !existing ||
                    new Date(doc.uploaded_at) >
                    new Date(existing.uploaded_at)
                ) {
                    acc[doc.document_type] = doc;
                }

                return acc;
            }, {})
    );
    console.log(documents)

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const handleDocumentClick = (doc) => {
        setCurrentDocument({
            url: `${BASE_URL.replace(/\/$/, '')}/${doc.file_path.replace(/^\//, '')}`,
            type: doc.file_extension,
            title: doc.document_type,
            dateUploaded: formatDate(doc.uploaded_at),
        });
        setIsOpen(true);
    };


    const closeModal = () => {
        setIsOpen(false);
        setCurrentDocument(null);
    };

    // const downloadDocument = (url, filename) => {
    //     const link = document.createElement('a');
    //     link.href = url;
    //     link.download = filename;
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    // };

    const openDocumentInNewTab = (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const profileImage = documents?.find(doc => doc.document_type === 'Profile Image');
    const profileImageUrl = profileImage
        ? `${BASE_URL.replace(/\/$/, '')}/${profileImage.file_path.replace(/^\//, '')}`
        : null;

    return (
        <Flex direction="column" p={8} width="100%">
            {/* Header */}
            <Flex alignItems="center">
                <ReusableAvatar src={profileImageUrl} />
                <Box>
                    <Text fontSize="2xl" fontWeight="bold" display="flex" alignItems="center">
                        {(user?.first_name && user?.last_name)
                            ? `${user.first_name} ${user.last_name}`
                            : user?.username || 'Driver Name'}
                        {status === 'Approved' && (
                            <>
                                <Icon as={BadgeCheck} size={12} color="green.500" stroke="white" ml={1} fill="green.500" />
                                <Text color="green.500" fontWeight="xs" fontSize="13px" ml={1}> Approved</Text>
                            </>
                        )}
                        {status === 'Pending' && (
                            <>
                                <Icon as={BadgeInfo} size={12} color="yellow.500" stroke="white" ml={1} fill="yellow.500" />
                                <Text color="yellow.500" fontWeight="xs" fontSize="13px" ml={1}> Pending</Text>
                            </>
                        )}
                        {status === 'Rejected' && (
                            <>
                                <Icon as={BadgeX} size={12} color="red.500" stroke="white" ml={1} fill="red.500" />
                                <Text color="red.500" fontWeight="xs" fontSize="13px" ml={1}> Rejected</Text>
                            </>
                        )}
                        {status === 'Suspended' && (
                            <>
                                <Icon as={BadgeX} size={12} color="orange.500" stroke="white" ml={1} fill="orange.500" />
                                <Text color="orange.500" fontWeight="xs" fontSize="13px" ml={1}> Suspended</Text>
                            </>
                        )}
                        {status === 'Deactivated' && (
                            <>
                                <Icon as={BadgeX} size={12} color="gray.500" stroke="white" ml={1} fill="gray.500" />
                                <Text color="gray.500" fontWeight="xs" fontSize="13px" ml={1}> Deactivated</Text>
                            </>
                        )}
                    </Text>
                    <Flex align="center" mt={1}>
                        <Icon
                            as={MapPin}
                            boxSize={3.5}
                            color="gray.500"
                            mr={1}
                        />

                        <Text fontSize="sm" color="gray.500">
                            {user?.village || 'No village specified'}, BW
                        </Text>
                    </Flex>
                </Box>
            </Flex>
            <Box
                mt={5}
                bgGradient="linear(to-r, blue.900, blue.700)"
                borderRadius="2xl"
                p={5}
                color="white"
                boxShadow="xl"
            >
                <Flex justify="space-between" align="center">

                    <Box>
                        <Text
                            fontSize="xs"
                            textTransform="uppercase"
                            opacity={0.8}
                            letterSpacing="1px"
                        >
                            Total Distance Travelled
                        </Text>

                        <Text
                            fontSize="4xl"
                            fontWeight="900"
                            lineHeight="1"
                            mt={1}
                        >
                            {
                                driverDistanceLoading
                                    ? "..."
                                    : Math.round(
                                        driverTotalDistance?.total_distance_km || 0
                                    )
                            }
                        </Text>

                        <Text
                            fontSize="sm"
                            opacity={0.85}
                            mt={1}
                        >
                            Kilometers Covered
                        </Text>
                    </Box>

                    <Flex
                        bg="whiteAlpha.200"
                        p={4}
                        borderRadius="full"
                        align="center"
                        justify="center"
                    >
                        <Icon as={Gauge} boxSize={10} />
                    </Flex>

                </Flex>
            </Box>
            <Flex mt={2} alignItems="center">
                <Icon as={Calendar} size={16} color="gray.500" mr={1} />
                <Text fontSize="sm" color="gray.500">
                    Date Created: {formatDate(created_at)}
                </Text>
            </Flex>

            <Divider my={6} />

            {/* Driver Info */}
            <Box mb={6}>
                <Text fontSize="md" fontWeight="500" color="gray.500" mb={2}>
                    Driver Info
                </Text>
                <Stack spacing={3}>
                    <InfoRow label="License Number" value={license_number} icon={IdCard} />
                    <InfoRow label="License Expiry Date" value={license_expiry_date} icon={Calendar} />
                    <InfoRow label="Route Preference" value={route_preference} icon={MapPin} />
                    <InfoRow label="Package Preference" value={package_preference} icon={Package} />
                </Stack>
            </Box>

             <Divider my={6} />

                <Box mb={6}>
                    <Flex
                        justify="space-between"
                        align="center"
                        mb={3}
                    >
                        <Text
                            fontSize="md"
                            fontWeight="500"
                            color="gray.500"
                        >
                            Wallet Information
                        </Text>

                        <GradientButton
                            height="34px"
                            fontSize="13px"
                            px={4}
                            onClick={() => onTopUp(driver)}
                        >
                            Complimentary Top-up
                        </GradientButton>
                    </Flex>

                    <Stack spacing={3}>
                        <InfoRow
                            label="Wallet Balance"
                            value={`P${Number(wallet?.balance || 0).toFixed(2)}`}
                            icon={Wallet}
                        />

                        <InfoRow
                            label="Wallet Plan"
                            value={wallet?.wallet_plan?.name || "No Wallet"}
                            icon={CreditCard}
                        />

                        <InfoRow
                            label="Remaining Deliveries"
                            value={wallet?.estimated_remaining_deliveries ?? "-"}
                            icon={Package}
                        />

                        <InfoRow
                            label="Total Top-ups"
                            value={`P${Number(wallet?.total_topups || 0).toFixed(2)}`}
                            icon={Coins}
                        />

                        <InfoRow
                            label="Deliveries Completed"
                            value={wallet?.total_deliveries_completed ?? 0}
                            icon={Truck}
                        />
                    </Stack>
                </Box>    

            <Divider my={6} />

            {/* Documents */}
            <Box mb={6}>
                <Text fontSize="md" fontWeight="500" color="gray.500" mb={2}>
                    Driver Documents
                </Text>
                {documents.length > 0 ? (
                    <Flex wrap="wrap" gap={4} overflowX="auto">
                        {documents.map((doc) => (
                            <Tooltip
                                label="Click to view"
                                hasArrow
                                placement="top"
                                key={doc.document_id}
                            >
                                <Box
                                    p={2}
                                    border="1px"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    display="flex"
                                    alignItems="center"
                                    _hover={{ cursor: 'pointer', bg: 'gray.100' }}
                                    onClick={() => handleDocumentClick(doc)}
                                    w="calc(50% - 8px)"
                                >
                                    <DocumentIcon type={doc.file_extension} />
                                    <Box ml={3}>
                                        <Text fontWeight="bold" color="gray.600">{doc.document_type}</Text>
                                        <Text color="gray.500" fontSize="sm">
                                            Uploaded: {formatDate(doc.uploaded_at)}
                                        </Text>
                                    </Box>
                                </Box>
                            </Tooltip>
                        ))}
                    </Flex>
                ) : (
                    <NoDataFound
                        message="No documents uploaded"
                        width="100px"
                        height="100px"
                        fontSize="sm"
                        fontWeight="bold"
                    />
                )}
            </Box>

            {/* Modal Viewer */}
            <Modal isOpen={isOpen} onClose={closeModal}>
                <ModalOverlay />
                <ModalContent maxWidth="500px" maxHeight="80vh" overflowY="auto">
                    <ModalHeader fontSize="16px">Document Viewer</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {currentDocument ? (
                            <>
                                <DocumentViewer documents={[currentDocument]} />
                                <IconButton
                                    aria-label="Download Document"
                                    icon={<Download />}
                                    onClick={() => openDocumentInNewTab(currentDocument.url)}
                                    bgGradient="linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)"
                                    color="white"
                                    mt={4}
                                    _hover={{ bgGradient: "linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)" }}
                                    boxShadow="md"
                                    borderRadius="full"
                                    size="lg"
                                />
                            </>
                        ) : (
                            <NoDataFound message="No document to display." />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Flex>
    );
};

export default ViewDriver;
